import { KaboodleToaster } from "components/common";
import Documents from "components/Documents/drawer/DocumentsDrawer";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import LoadingIcon from "components/Loading/LoadingIcon";
import TaskModal from "pages/tasks/components/modals/TaskModal";
import CodeDirectoryDrawer from "components/CodeDirectory/CodeDirectoryDrawer";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { clearCurrentTask } from "reducers/files";
import { setIsLoading, setShowTaskModal } from "reducers/main";
import { usePatchClaimantByIdMutation } from "services/claimant-api";
import {
  useGetCostProjectionByIdQuery,
  usePatchCostProjectionByIdMutation,
} from "services/cost-projection-api";
import {
  usePatchDateOfInjuryByIdMutation,
  usePatchServiceDateOfInjuryByIdMutation,
} from "services/doi-api";
import { usePatchFileByIdMutation } from "services/file-api.ts";
import {
  useGetPartABPlanCoveragesQuery,
  useGetPartCDPlanCoveragesQuery,
} from "services/part-coverage-api";
import { useToken } from "utils/context";
import { useDispatch, useSelector } from "utils/redux";

import { setCoverageFormValues } from "../shared/coverage-utils";
import { FutureMedicalBackgroundJobs } from "../shared/FutureMedicalBackgroundJobs";
import FutureMedicalClaimantInformation from "../shared/FMClaimantInformation";
import FutureMedicalClinicianNotes from "../shared/FMClinicianNotes";
import FutureMedicalLegalNotes from "../shared/FMLegalNotes";
import FutureMedicalRightPanel from "../shared/FMRightPanel";
import FutureMedicalMedicalHistoryReview from "../shared/medical-history/FMMedicalHistoryReview";
import FMRxHistory from "../shared/medical-history/FMRxHistory";
import CostProjectionPageContainer from "./CostProjectionPageContainer";

import AdjusterInformationForm from "pages/files/sections/AdjusterInformation";
import DefenseAttorneyInformationForm from "pages/files/sections/DefenseAttorneyInformation";

const CostProjectionSectionPage = () => {
  const navigate = useNavigate();
  const { reset, setValue, clearErrors, setError } = useFormContext();
  const dispatch = useDispatch();
  const { costProjectionSection } = useParams<{
    costProjectionSection?: string;
  }>();

  const { accessToken } = useToken();
  const { costProjectionId } = useParams<{ costProjectionId: string }>();
  const showTaskModal = useSelector((state) => state.main.showTaskModal);

  const {
    data: costProjection,
    isLoading,
    isError,
  } = useGetCostProjectionByIdQuery(
    { costProjectionId: costProjectionId },
    { skip: !accessToken || !costProjectionId },
  );

  const [patchCostProjectionById] = usePatchCostProjectionByIdMutation();
  const [patchClaimantById] = usePatchClaimantByIdMutation();
  const [patchServiceDateOfInjuryById] =
    usePatchServiceDateOfInjuryByIdMutation();
  const [patchDateOfInjuryById] = usePatchDateOfInjuryByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fileId = searchParams.get("file_id");

  const { selectedServiceDoi } = useServiceDOI(costProjection);

  // fetch plan coverages by file_id
  const { data: partABCoverages = [] } = useGetPartABPlanCoveragesQuery(
    { file_id: costProjection?.file?.id },
    { skip: !costProjection?.file?.id },
  );

  const { data: partCDCoverages = [] } = useGetPartCDPlanCoveragesQuery(
    { file_id: costProjection?.file?.id },
    { skip: !costProjection?.file?.id },
  );

  const handleClose = useCallback(() => {
    dispatch(clearCurrentTask());
    dispatch(setShowTaskModal(false));
  }, [dispatch]);

  const modal = useMemo(() => {
    return (
      <TaskModal
        isOpen={showTaskModal}
        onClose={handleClose}
        fileId={costProjection?.file?.id?.toString()}
        serviceId={costProjection?.service}
      />
    );
  }, [showTaskModal, handleClose, costProjection]);

  useEffect(() => {
    if (!isLoading && (!costProjection || isError)) {
      navigate("/404");
    }
  }, [costProjection, isLoading, isError, history]);

  useEffect(() => {
    if (!isLoading && costProjection) {
      reset(costProjection, { keepDirtyValues: true });
      // ensure we have arrays even if data is undefined
      const planCoverages = {
        part_a_b_coverages: Array.isArray(partABCoverages)
          ? partABCoverages
          : [],
        part_c_d_coverages: Array.isArray(partCDCoverages)
          ? partCDCoverages
          : [],
      };
      setCoverageFormValues(planCoverages, setValue);
    }
  }, [
    costProjection,
    reset,
    isLoading,
    setValue,
    partABCoverages,
    partCDCoverages,
  ]);

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const getField = (field: string) =>
    field?.includes(".") ? field.split(".")[1] : field;

  const handleSaveCostProjection = useCallback(
    async (field: string | null, value: unknown) => {
      if (costProjectionId && accessToken) {
        try {
          if (field?.startsWith?.("claimant.")) {
            // handle claimant fields
            await patchClaimantById({
              id: costProjection.claimant.id,
              data: {
                [getField(field)]: value,
              },
            }).unwrap();
          } else if (field?.startsWith?.("service_date_of_injury.")) {
            // handle service date of injury fields
            if (selectedServiceDoi?.id) {
              await patchServiceDateOfInjuryById({
                id: selectedServiceDoi.id.toString(),
                data: {
                  [getField(field)]: value,
                },
              }).unwrap();
            } else {
              console.error(
                "Cannot update DOI: selectedServiceDoi is null or id is missing",
              );
            }
          } else if (field?.startsWith?.("date_of_injury.")) {
            // handle date of injury fields
            if (selectedServiceDoi?.date_of_injury) {
              // The actual DateOfInjury ID is in the date_of_injury field of the ServiceDateOfInjury object
              await patchDateOfInjuryById({
                id: selectedServiceDoi.date_of_injury.toString(),
                data: {
                  [getField(field)]: value,
                },
              }).unwrap();
            } else {
              KaboodleToaster.error(
                "Cannot update DOI: selectedServiceDoi is null or date_of_injury is missing",
              );
            }
          } else if (field?.startsWith?.("file.")) {
            // handle file fields
            await patchFileById({
              id: fileId?.toString() || costProjection?.file?.id.toString(),
              data: {
                [getField(field)]: value,
              },
            });
          } else {
            // if it's a bulk update (null/empty field), pass value directly
            const data = !field ? value : { [field]: value };
            await patchCostProjectionById({
              costProjectionId,
              data,
            }).unwrap();
          }
          clearErrors(field || "");
        } catch (error) {
          console.error("Error saving Cost Projection:", error);
          setError(field || "", {
            type: error.status,
            message: error.data[field]?.[0] || "An error occurred",
          });
        }
      }
    },
    [
      costProjection,

      patchCostProjectionById,
      patchClaimantById,
      patchDateOfInjuryById,
      patchFileById,
      costProjectionId,
      clearErrors,
      setError,
      fileId,
      patchServiceDateOfInjuryById,
      selectedServiceDoi,
    ],
  );

  const isClientDeliveryPage =
    costProjectionSection === "lcp-to-client" ||
    costProjectionSection === "mcp-to-client";

  if (isLoading) {
    return <LoadingIcon />;
  }

  if (!costProjection) {
    return null;
  }

  // on legal and medical review pages, we don't need the claimant information section, so make layout full
  const isReviewPage =
    costProjectionSection === "legal-review" ||
    costProjectionSection === "medical-review";

  return (
    <CostProjectionPageContainer
      layout={isReviewPage ? "full" : "split"}
      service={costProjection}
    >
      {modal}
      {!isReviewPage && !isClientDeliveryPage && (
        <FutureMedicalClaimantInformation
          onSave={handleSaveCostProjection}
          service={costProjection}
          section={costProjectionSection}
        />
      )}
      {isClientDeliveryPage && (
        <>
          <div className={`FutureMedical_Adjuster_DefenseAttorneyInformation`}>
            <div className="swim-headers">
              Adjuster and Defense Attorney Information
            </div>
            <div className="swim-column">
              <AdjusterInformationForm
                fileFromServiceId={costProjection.file.id.toString()}
              />
              <DefenseAttorneyInformationForm
                fileId={costProjection.file.id.toString()}
              />
            </div>
          </div>
        </>
      )}
      <FutureMedicalRightPanel
        section={costProjectionSection}
        service={costProjection}
        onSave={handleSaveCostProjection}
      />
      <FutureMedicalClinicianNotes
        service={costProjection}
        onSave={handleSaveCostProjection}
      />
      <FutureMedicalLegalNotes
        service={costProjection}
        onSave={handleSaveCostProjection}
      />
      <ClientInformationDrawer service={costProjection} />
      <FutureMedicalBackgroundJobs fileId={costProjection?.file?.id} />
      <FutureMedicalMedicalHistoryReview service={costProjection} />
      <FMRxHistory service={costProjection} />
      <Documents
        fileId={costProjection?.file?.id}
        serviceId={costProjection?.id}
        serviceType={costProjection?.type?.toLowerCase()}
      />
      <CodeDirectoryDrawer />
    </CostProjectionPageContainer>
  );
};

export default CostProjectionSectionPage;
